import { FlaskConical } from 'lucide-react';
import { Button, type ButtonVariant } from '@/components/ui';
import type { PointPair } from '@/types';
import { remainingActivePairs } from './adjustmentDisplay';

/**
 * Opens a rejection trial for a recommended set. Applying happens only from
 * the trial review, after the field-evidence prompt.
 */
export const RejectionAction = ({
  ids,
  labels,
  pointPairs,
  onPreview,
  disabledReason,
  variant = 'secondary',
  compact = false,
  className,
}: {
  ids: number[];
  /** Pair labels for the accessible name of compact buttons. */
  labels?: string[];
  pointPairs: PointPair[];
  onPreview: (ids: number[]) => void;
  /** Why the recommendation cannot be previewed (e.g. outdated results). */
  disabledReason?: string;
  variant?: ButtonVariant;
  /** Short "Preview" label for table rows. */
  compact?: boolean;
  className?: string;
}) => {
  const remaining = remainingActivePairs(pointPairs, ids);
  const reason =
    disabledReason ??
    (!ids.length
      ? 'Nothing to reject'
      : remaining < 2
        ? `Only ${remaining} ${remaining === 1 ? 'pair' : 'pairs'} would remain in the fit`
        : undefined);
  const name = `Preview ${ids.length}-pair rejection`;
  return (
    <Button
      size="sm"
      variant={variant}
      leadingIcon={<FlaskConical />}
      disabled={reason !== undefined}
      disabledReason={reason}
      aria-label={
        compact && labels?.length ? `${name}: ${labels.join(', ')}` : undefined
      }
      onClick={() => onPreview(ids)}
      className={className}
    >
      {compact ? 'Preview' : name}
    </Button>
  );
};
